import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Login berhasil");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err, "Login gagal"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: "radial-gradient(1000px 700px at 20% -10%, rgba(0,92,255,0.18), transparent 55%), #020713" }}>
      {/* left hero */}
      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1624880056652-c7993c99190f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwbHV4dXJ5JTIwY2FyJTIwY2xvc2UlMjB1cCUyMHdoaXRlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODc2MTQxNjV8MA&ixlib=rb-4.1.0&q=85"
          alt="Titanium Parts"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(2,7,19,0.82), rgba(0,40,90,0.75))" }} />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center border border-white/20">
              <span className="font-head font-extrabold text-[var(--gold)] text-2xl">T</span>
            </div>
            <span className="font-head font-bold text-lg tracking-tight">TITANIUM PARTS</span>
          </div>
          <div>
            <h1 className="font-head text-4xl xl:text-5xl font-extrabold leading-tight glow-text">
              Right Part.<br />Right Car.<br /><span className="text-[var(--neon-cyan)]">Right Control.</span>
            </h1>
            <p className="text-white/70 mt-4 max-w-md">
              Sistem kontrol sparepart berbasis SPK dengan AI OCR. SPK → Required Parts → Receiving → Matching → Complete.
            </p>
          </div>
          <div className="text-white/40 text-xs uppercase tracking-[0.2em]">SPK • Receiving • Warehouse Control</div>
        </div>
      </div>

      {/* right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm neon-card p-8">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center border border-[rgba(0,200,255,0.5)]" style={{ background: "rgba(0,180,255,0.1)", boxShadow: "0 0 14px rgba(0,200,255,0.4)" }}>
              <span className="font-head font-extrabold text-[var(--gold)] text-xl">T</span>
            </div>
            <span className="font-head font-bold text-white">TITANIUM PARTS</span>
          </div>

          <h2 className="font-head text-3xl font-bold text-white glow-text">Masuk</h2>
          <p className="text-gray-500 mt-1 mb-8 text-sm">Silakan login untuk mengakses sistem.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wide text-gray-500">Email</Label>
              <Input
                id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email" required placeholder="nama@bengkeltitanium.com" className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wide text-gray-500">Password</Label>
              <Input
                id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password" required placeholder="••••••••" className="mt-1.5 h-11"
              />
            </div>
            <Button
              type="submit" disabled={loading} data-testid="login-submit"
              className="w-full h-11 bg-gray-900 hover:bg-gray-800 text-white font-semibold active:scale-[0.99] transition-transform"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Masuk"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
